import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SyncIcon from '@mui/icons-material/Sync';
import api from '../../../services/useApi';
import PonPortsOltSelect from '../../../components/selects/PonPortsOltSelect';

interface Profile {
  id: number;
  tipo: 'lineProfile' | 'srvProfile';
  sfp: string[] | null;
  nameProfile: string;
  idProfile: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProfilesTabProps {
  oltId: number;
}

const ProfilesTab: React.FC<ProfilesTabProps> = ({ oltId }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [formData, setFormData] = useState({
    tipo: 'lineProfile' as 'lineProfile' | 'srvProfile',
    sfp: [] as string[],
    nameProfile: '',
    idProfile: ''
  });  
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [oltId]);

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/olts/${oltId}/profiles`);
      if(response.data.success){
        setProfiles(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar profiles:', error);
      setError('Erro ao carregar profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (profile?: Profile) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        tipo: profile.tipo,
        sfp: profile.sfp || [],
        nameProfile: profile.nameProfile,
        idProfile: profile.idProfile
      });
    } else {
      setEditingProfile(null);
      setFormData({
        tipo: 'lineProfile',
        sfp: [],
        nameProfile: '',
        idProfile: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProfile(null);
    setFormData({
      tipo: 'lineProfile',
      sfp: [],
      nameProfile: '',
      idProfile: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.nameProfile || !formData.idProfile) {
      setError('Todos os campos obrigatórios devem ser preenchidos');
      return;
    }

    try {
      if (editingProfile) {
        await api.put(`/olts/${oltId}/profiles/${editingProfile.id}`, {
          ...formData,
          sfp: formData.sfp.length > 0 ? formData.sfp : null
        });
        toast.success('Profile atualizado com sucesso!');
      } else {
        await api.post(`/olts/${oltId}/profiles`, {
          ...formData,
          sfp: formData.sfp.length > 0 ? formData.sfp : null
        });
        toast.success('Profile criado com sucesso!');
      }

      handleCloseDialog();
      fetchProfiles();
      setError(null);
    } catch (error: any) {
      console.error('Erro ao salvar profile:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao salvar profile';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (profileId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este profile?')) {
      return;
    }

    try {
      await api.delete(`/olts/${oltId}/profiles/${profileId}`);
      fetchProfiles();
      setError(null);
      toast.success('Profile deletado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao deletar profile:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao deletar profile';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleSyncProfiles = async () => {
    setSyncing(true);
    setError(null);
    try {
      const response = await api.post(`/olts/${oltId}/profiles/sync`);
      if (response.data.success) {
        toast.success('Profiles sincronizados com sucesso!');
        fetchProfiles();
      } else {
        toast.error('Erro ao sincronizar profiles');
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar profiles:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao sincronizar profiles';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const getTipoColor = (tipo: string) => {
    return tipo === 'lineProfile' ? 'primary' : 'secondary';
  };

  const getTipoLabel = (tipo: string) => {
    return tipo === 'lineProfile' ? 'Line Profile' : 'Service Profile';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          Profiles da OLT
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncProfiles}
            disabled={syncing}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar Profiles'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Novo Profile
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{
        '& .MuiTableCell-root': {
          py: 0.5,
          px: 1.5,
          fontSize: '0.875rem',
          lineHeight: 1.2
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          py: 0.75,
          fontWeight: 600,
          fontSize: '0.8rem'
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell>Nome do Profile</TableCell>
              <TableCell>ID do Profile</TableCell>
              <TableCell>SFP (Slot/Porta)</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum profile encontrado
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell>
                    <Chip
                      label={getTipoLabel(profile.tipo)}
                      color={getTipoColor(profile.tipo)}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {profile.nameProfile}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {profile.idProfile}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {profile.sfp && profile.sfp.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {profile.sfp.map((port, index) => (
                          <Chip key={index} label={port} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 18 }} />
                        ))}                  
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {new Date(profile.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(profile)}
                      color="primary"
                      sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(profile.id)}
                      color="error"
                      sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para criar/editar profile */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProfile ? 'Editar Profile' : 'Novo Profile'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo"
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'lineProfile' | 'srvProfile' })}
              >
                <MenuItem value="lineProfile">Line Profile</MenuItem>
                <MenuItem value="srvProfile">Service Profile</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Nome do Profile"
              value={formData.nameProfile}
              onChange={(e) => setFormData({ ...formData, nameProfile: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="ID do Profile"
              value={formData.idProfile}
              onChange={(e) => setFormData({ ...formData, idProfile: e.target.value })}
              fullWidth
              required
            />

            <PonPortsOltSelect
              oltId={oltId}
              value={formData.sfp}
              onChange={(value) => setFormData({ ...formData, sfp: Array.isArray(value) ? value : [value] })}
              label="SFP (Slot/Porta)"
              required={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProfile ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilesTab; 